import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiTrendingUp, FiPhone, FiRefreshCw, FiHome } = FiIcons;

const ResultsPage = () => {
  const location = useLocation();
  const score = location.state?.score || 0;

  // Determine result category based on score
  const getResult = (score) => {
    if (score <= 14) {
      return {
        category: 'Entwicklungsbedarf',
        color: 'red',
        icon: FiTarget,
        title: 'Ihre Karriereplanung benötigt noch Struktur',
        description: 'Ihre beruflichen Ziele und Strategien sind noch nicht klar definiert. Das ist völlig normal und bietet eine gute Ausgangslage für eine strukturierte Karriereentwicklung.',
        recommendations: [
          'Nehmen Sie sich Zeit für eine Standortbestimmung',
          'Definieren Sie kurz- und langfristige Ziele',
          'Erstellen Sie einen konkreten Aktionsplan',
          'Suchen Sie professionelle Unterstützung'
        ]
      };
    } else if (score <= 27) {
      return {
        category: 'Teilweise entwickelt',
        color: 'yellow',
        icon: FiTrendingUp,
        title: 'Ihre Karriereplanung ist teilweise strukturiert',
        description: 'Sie haben bereits einige Klarheit über Ihre beruflichen Ziele, aber es gibt noch Bereiche, die von einer weiteren Strukturierung profitieren könnten.',
        recommendations: [
          'Verfeinern Sie Ihre bestehenden Ziele',
          'Entwickeln Sie konkretere Strategien',
          'Identifizieren Sie mögliche Hindernisse',
          'Erwägen Sie eine professionelle Beratung'
        ]
      };
    } else {
      return {
        category: 'Gut entwickelt',
        color: 'green',
        icon: FiTarget,
        title: 'Ihre Karriereplanung ist gut strukturiert',
        description: 'Sie haben eine klare Vorstellung von Ihren beruflichen Zielen und verfügen über durchdachte Strategien. Ihre Planung zeigt eine gute Balance zwischen Struktur und Flexibilität.',
        recommendations: [
          'Überprüfen Sie regelmäßig Ihre Fortschritte',
          'Bleiben Sie offen für neue Möglichkeiten',
          'Nutzen Sie Ihr Planungstalent als Stärke',
          'Unterstützen Sie andere bei ihrer Karriereplanung'
        ]
      };
    }
  };

  const result = getResult(score);

  const colorClasses = {
    red: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      button: 'bg-red-600 hover:bg-red-700'
    },
    yellow: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      border: 'border-yellow-200',
      button: 'bg-yellow-600 hover:bg-yellow-700'
    },
    green: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-200',
      button: 'bg-green-600 hover:bg-green-700'
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Ihre Testergebnisse</h1>
          <Link 
            to="/impressum" 
            className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
          >
            Impressum
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* Score Display */}
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
              className="inline-block"
            >
              <div className={`w-24 h-24 ${colorClasses[result.color].bg} rounded-full flex items-center justify-center mb-4 mx-auto`}>
                <SafeIcon icon={result.icon} className={`w-12 h-12 ${colorClasses[result.color].text}`} />
              </div>
            </motion.div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{result.title}</h2>
            <p className="text-lg text-gray-600">Gesamtpunktzahl: {score} von 45 Punkten</p>
          </div>

          {/* Result Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className={`bg-white rounded-lg shadow-lg border-l-4 ${colorClasses[result.color].border} p-8`}
          >
            <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-4 ${colorClasses[result.color].bg} ${colorClasses[result.color].text}`}>
              {result.category}
            </div>
            
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              {result.description}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Empfehlungen für Sie:</h3>
            <ul className="space-y-2">
              {result.recommendations.map((recommendation, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
                  className="flex items-start"
                >
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700">{recommendation}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Professional Consultation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <h3 className="text-xl font-semibold text-blue-900 mb-3">
              Interesse an einer professionellen Analyse?
            </h3>
            <p className="text-blue-800 mb-4">
              Für eine umfassende, zertifizierte Karriereberatung und individuelle Entwicklungsplanung 
              stehen wir Ihnen gerne zur Verfügung.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center text-blue-800">
                <SafeIcon icon={FiPhone} className="w-5 h-5 mr-2" />
                <span className="font-medium">Telefon: 069-21939559</span>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/test"
              className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <SafeIcon icon={FiRefreshCw} className="w-5 h-5 mr-2" />
              Test wiederholen
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <SafeIcon icon={FiHome} className="w-5 h-5 mr-2" />
              Zur Startseite
            </Link>
          </motion.div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.5 }}
            className="text-center text-sm text-gray-500 max-w-2xl mx-auto"
          >
            <p>
              Dieser Test dient der Selbstreflexion und ersetzt keine professionelle Karriereberatung. 
              Die Ergebnisse sind als Orientierungshilfe zu verstehen und können je nach persönlicher 
              Situation variieren.
            </p>
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
};

export default ResultsPage;